<template>
  <div class="pt-[12px] md:pt-[14px] sm:flex sm:items-center justify-between">
    <p class="mb-0 text-sm">
      {{ directT('dashboard.showingResults').replace('{start}', startItem.toString()).replace('{end}', endItem.toString()).replace('{total}', total.toString()) }}
    </p>
    <ol class="mt-[10px] sm:mt-0" v-if="totalPages > 1">
      <!-- Previous Page Button -->
      <li
        class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
      >
        <button
          @click="goToPrevPage"
          :disabled="currentPage <= 1"
          :class="[
            'w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border transition-all',
            currentPage <= 1 
              ? 'border-gray-200 text-gray-400 cursor-not-allowed dark:border-[#2b3a5e] dark:text-gray-600' 
              : 'border-gray-100 dark:border-[#172036] hover:bg-primary-500 hover:text-white hover:border-primary-500'
          ]"
        >
          <span class="opacity-0"> 0 </span>
          <i
            class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
          >
            chevron_left
          </i>
        </button>
      </li>
      
      <!-- Page Numbers -->
      <template v-for="page in displayedPages" :key="page">
        <!-- Ellipsis -->
        <li v-if="page === '...'"
          class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
        >
          <span class="w-[31px] h-[31px] block leading-[29px] relative text-center">...</span>
        </li>
        
        <!-- Page Number -->
        <li v-else
          class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
        >
          <button
            @click="goToPage(Number(page))"
            :class="[
              'w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border',
              currentPage === Number(page)
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500'
            ]"
          >
            {{ page }}
          </button>
        </li>
      </template>
      
      <!-- Next Page Button -->
      <li
        class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
      >
        <button
          @click="goToNextPage"
          :disabled="currentPage >= totalPages"
          :class="[
            'w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border transition-all',
            currentPage >= totalPages
              ? 'border-gray-200 text-gray-400 cursor-not-allowed dark:border-[#2b3a5e] dark:text-gray-600'
              : 'border-gray-100 dark:border-[#172036] hover:bg-primary-500 hover:text-white hover:border-primary-500'
          ]"
        >
          <span class="opacity-0"> 0 </span>
          <i
            class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
          >
            chevron_right
          </i>
        </button>
      </li>
    </ol>
  </div>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent, computed } from "vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | string[] | TranslationObject;
};

export default defineComponent({
  name: "Pagination",
  props: {
    items: {
      type: [Number, String],
      default: 10,
      validator: (value: number | string) => {
        // Convert to number for validation
        const numValue = typeof value === 'string' ? parseInt(value) : value;
        return !isNaN(numValue) && numValue > 0;
      }
    },
    total: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      default: 1
    }
  },
  emits: ['page-change'],
  setup(props, { emit }) {
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject | string | string[] = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || typeof result === 'string' || Array.isArray(result) || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject | string | string[] = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || typeof enResult === 'string' || Array.isArray(enResult) || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject | string | string[];
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject | string | string[];
      }
      
      if (typeof result === 'string') {
        return result;
      } else if (Array.isArray(result)) {
        return result.join(', '); // Join array elements with comma
      } else {
        return key; // Return the key if result is not a string or array
      }
    };

    // Helper to ensure items is a number
    const itemsAsNumber = computed(() => {
      return typeof props.items === 'string' ? parseInt(props.items) : props.items;
    });

    // Calculate total pages
    const totalPages = computed(() => Math.ceil(props.total / itemsAsNumber.value));

    // Calculate start and end items for display
    const startItem = computed(() => {
      if (props.total === 0) return 0;
      return (props.currentPage - 1) * itemsAsNumber.value + 1;
    });

    const endItem = computed(() => {
      return Math.min(props.currentPage * itemsAsNumber.value, props.total);
    });

    // Determine which pages to display
    const displayedPages = computed(() => {
      const total = totalPages.value;
      const current = props.currentPage;
      const pages = [];
      
      if (total <= 7) {
        // Show all pages if there are 7 or fewer
        for (let i = 1; i <= total; i++) {
          pages.push(i.toString());
        }
      } else {
        // Always show first page
        pages.push('1');
        
        if (current <= 3) {
          // Show first 5 pages then ellipsis then last page
          pages.push('2', '3', '4', '5', '...', total.toString());
        } else if (current >= total - 2) {
          // Show first page then ellipsis then last 5 pages
          pages.push('...', (total - 4).toString(), (total - 3).toString(), (total - 2).toString(), (total - 1).toString(), total.toString());
        } else {
          // Show first page, ellipsis, current-1, current, current+1, ellipsis, last page
          pages.push('...', (current - 1).toString(), current.toString(), (current + 1).toString(), '...', total.toString());
        }
      }
      
      return pages;
    });

    // Page navigation functions
    const goToPage = (page: number) => {
      if (page !== props.currentPage) {
        emit('page-change', page);
      }
    };

    const goToPrevPage = () => {
      if (props.currentPage > 1) {
        emit('page-change', props.currentPage - 1);
      }
    };

    const goToNextPage = () => {
      if (props.currentPage < totalPages.value) {
        emit('page-change', props.currentPage + 1);
      }
    };

    return {
      totalPages,
      startItem,
      endItem,
      displayedPages,
      goToPage,
      goToPrevPage,
      goToNextPage,
      directT
    };
  }
});
</script>
